import BigNumber from "bignumber.js";
import config from "../config";
import { useTokenHelpers } from "./useTokenHelpers";
import DirectExchange from "../../src/assets/abi/directExchangeWithFee/DirectExchangeWithFee.json";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";
import { config as wagmiConfig } from "../../wagmi.config";
import useLazyReadContractAsync from "./useLazyReadContractAsync";
import { useUnifiedWallet } from "../providers/UnifiedWalletProvider";

const useDirectExchangeHelpers = () => {
  const { getBalanceOf } = useTokenHelpers();
  const { walletApi } = useUnifiedWallet();
  const { lazyReadContract } = useLazyReadContractAsync();

  const getAllExchangeInfo = async () => {
    const { directExchange } = config;

    const result = await directExchange.reduce(async (prev, cur, index) => {
      let acc: any = await prev;

      const response = await getExchangeInfo(index, cur);

      if (response) acc = [...acc, response];

      return acc;
    }, Promise.resolve([]));

    return result;
  };

  const getTokenRationOmniPrice = async () => {
    const { directExchange } = config;

    const omniDirectExchange = directExchange[1];
    const account = walletApi.account.current;
    if (account === undefined) return null;
    if (!omniDirectExchange) return {};
    const { contractAddress } = omniDirectExchange as any;
    const tokenRatio = await getTokenRatio(contractAddress);
    return tokenRatio
      ? BigNumber("1").dividedBy(BigNumber(tokenRatio).div(1e18))
      : BigNumber("0");
  };

  const getTokenRatio = async (contractAddress: string) => {
    try {
      const rate = await lazyReadContract(
        DirectExchange,
        contractAddress,
        "tokenRatio",
        []
      );
      const rateBN = BigNumber(rate?.toString() ?? "0");
      return rateBN;
    } catch (e) {
      return null;
    }
  };

  const getPauseInfo = async (contractAddress: string) => {
    try {
      const paused = await lazyReadContract(
        DirectExchange,
        contractAddress,
        "paused",
        []
      );
      return Boolean(paused);
    } catch (e) {
      return null;
    }
  };

  const getFeeRate = async (contractAddress: string) => {
    try {
      const feeRate = await lazyReadContract(
        DirectExchange,
        contractAddress,
        "feeRate",
        []
      );
      
      const feeRateBN = BigNumber(feeRate?.toString() ?? "0");
      return feeRateBN;
    } catch (e) {
      return BigNumber("0");
    }
  };

  const getExchangeInfo = async (index: number, data: any) => {
    const { directExchange } = config;
    const account = walletApi.account.current;
    if (account === undefined) return null;
    data = data || directExchange[index];
    if (!data) return {};
    const { contractAddress, destinationToken, originToken } = data;

    const promises = [
      getTokenRatio(contractAddress),
      getBalanceOf(destinationToken.code, contractAddress),
      getPauseInfo(contractAddress),
      getFeeRate(contractAddress),
    ];

    const extendPromises = account
      ? [
          getBalanceOf(originToken.code, account),
          getBalanceOf(destinationToken.code, account),
        ]
      : [];

    const [
      tokenRatio,
      poolBalances,
      paused,
      feeRate,
      originBalances,
      destinationBalances,
    ] = await Promise.all([...promises, ...extendPromises]);
    const result = {
      poolBalances: poolBalances
        ? BigNumber(poolBalances?.toString() ?? "0").div(1e18)
        : BigNumber("0"),
      myOrigintokenBalance: originBalances
        ? BigNumber(originBalances?.toString() ?? "0")
        : BigNumber("0"),
      myDestinationTokenBalance: destinationBalances
        ? BigNumber(destinationBalances?.toString() ?? "0")
        : BigNumber("0"),
      exchangeRate: tokenRatio
        ? BigNumber(tokenRatio?.toString() ?? "0").div(1e18)
        : BigNumber("0"),
      feeRate: feeRate || BigNumber("0"),
      paused,
    };
    return result;
  };

  const exchange = async (
    contractAddress: string,
    account: string,
    inputAmount: BigNumber,
    events: any
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: DirectExchange,
        address: contractAddress as `0x${string}`,
        functionName: "exchange",
        args: [BigInt(inputAmount.toFixed(0))],
      });
      
      // Wait for transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
      
      events.onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (e) {
      events.onError(e);
      console.log("Err: error exchange:", e);
      return null;
    }
  };

  return { getAllExchangeInfo, exchange, getTokenRationOmniPrice };
};

export default useDirectExchangeHelpers;
